import {
  ArrowRight,
  Box,
  Check,
  ChevronRight,
  CirclePlay,
  FileCheck2,
  Menu,
  PackageCheck,
  PenTool,
  Ruler,
  Sparkles,
  X,
} from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Logo } from "../components/Logo";

const workflow = [
  { icon: Ruler, number: "01", title: "Meça", text: "Capture dimensões e transforme referências em uma base precisa." },
  { icon: PenTool, number: "02", title: "Planeje", text: "Desenhe em 2D e valide cada decisão em uma cena 3D sincronizada." },
  { icon: FileCheck2, number: "03", title: "Documente", text: "Gere anotações, etiquetas e relatórios na mesma revisão." },
  { icon: PackageCheck, number: "04", title: "Produza", text: "Prepare peças e fabricação com rastreabilidade e revisão humana." },
];

export function PublicPage() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [demoMode, setDemoMode] = useState<"2d" | "3d">("3d");

  return (
    <div className="public-page">
      <header className="public-nav">
        <Link to="/"><Logo /></Link>
        <nav className={menuOpen ? "public-nav__links public-nav__links--open" : "public-nav__links"}>
          <a href="#produto" onClick={() => setMenuOpen(false)}>Produto</a>
          <a href="#fluxo" onClick={() => setMenuOpen(false)}>Como funciona</a>
          <Link to="/pricing" onClick={() => setMenuOpen(false)}>Planos</Link>
          <Link to="/downloads" onClick={() => setMenuOpen(false)}>Downloads</Link>
          <Link to="/help/pt-BR" onClick={() => setMenuOpen(false)}>Ajuda</Link>
        </nav>
        <div className="public-nav__actions">
          <Link className="button button--ghost" to="/auth">Entrar</Link>
          <Link className="button button--primary" to="/auth">Começar agora <ArrowRight size={16} /></Link>
          <button className="icon-button public-menu" onClick={() => setMenuOpen((value) => !value)} aria-label="Alternar menu">
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </header>

      <main>
        <section className="hero">
          <div className="hero__copy">
            <span className="eyebrow"><Sparkles size={15} /> Planejamento técnico, sem rupturas</span>
            <h1>Projete ambientes que já nascem <em>prontos para produzir.</em></h1>
            <p>
              Do croqui ao 3D, do material ao plano de corte: uma plataforma
              precisa para equipes de arquitetura e marcenaria.
            </p>
            <div className="hero__actions">
              <Link className="button button--primary button--large" to="/auth">Criar meu primeiro projeto <ArrowRight size={18} /></Link>
              <a className="button button--secondary button--large" href="#produto"><CirclePlay size={18} /> Ver como funciona</a>
            </div>
            <div className="hero__proof">
              <span><Check size={15} /> Comece gratuitamente</span>
              <span><Check size={15} /> 2D e 3D sincronizados</span>
              <span><Check size={15} /> Dados no seu workspace</span>
            </div>
          </div>
          <div className="hero-product" id="produto">
            <div className="hero-product__window">
              <div className="hero-product__chrome">
                <Logo compact />
                <span>Residencial Horizonte</span>
                <div>
                  <button onClick={() => setDemoMode("2d")} className={demoMode === "2d" ? "active" : ""}>Planta</button>
                  <button onClick={() => setDemoMode("3d")} className={demoMode === "3d" ? "active" : ""}>3D</button>
                </div>
              </div>
              <div className="hero-product__workspace">
                <div className="hero-product__tools">
                  <span /><span /><span /><span /><span />
                </div>
                {demoMode === "3d" ? (
                  <div className="demo-room">
                    <div className="demo-grid" />
                    <div className="demo-wall demo-wall--left" />
                    <div className="demo-wall demo-wall--right" />
                    <div className="demo-cabinet demo-cabinet--one" />
                    <div className="demo-cabinet demo-cabinet--two" />
                    <div className="demo-table" />
                    <div className="demo-rug" />
                  </div>
                ) : (
                  <div className="demo-plan">
                    <div className="plan-wall plan-wall--top" />
                    <div className="plan-wall plan-wall--right" />
                    <div className="plan-wall plan-wall--bottom" />
                    <div className="plan-wall plan-wall--left" />
                    <div className="plan-door" />
                    <div className="plan-label">COZINHA<br /><strong>18,4 m²</strong></div>
                    <span className="measure measure--top">5.200</span>
                    <span className="measure measure--right">3.540</span>
                  </div>
                )}
                <div className="hero-product__panel">
                  <strong>Propriedades</strong>
                  <label>Largura <span>800 mm</span></label>
                  <label>Altura <span>720 mm</span></label>
                  <label>Material <i /></label>
                </div>
              </div>
            </div>
            <div className="floating-metric floating-metric--top"><Box size={17} /> <span><strong>3D sincronizado</strong>Atualização instantânea</span></div>
            <div className="floating-metric floating-metric--bottom"><span className="metric-dot" /><span><strong>Revisão salva</strong>Agora mesmo</span></div>
          </div>
        </section>

        <section className="trust-strip">
          <span>Feito para quem transforma espaço em realidade</span>
          <div>
            <strong>ARQUITETURA</strong><strong>MARCENARIA</strong><strong>INTERIORES</strong><strong>PRODUÇÃO</strong>
          </div>
        </section>

        <section className="workflow-section" id="fluxo">
          <div className="section-heading">
            <span className="eyebrow">Fluxo completo</span>
            <h2>Uma linha contínua entre ideia e execução.</h2>
            <p>Menos retrabalho, mais clareza e uma fonte única de verdade.</p>
          </div>
          <div className="workflow-grid">
            {workflow.map(({ icon: Icon, number, title, text }, index) => (
              <article key={title}>
                <span className="workflow-number">{number}</span>
                <span className="workflow-icon"><Icon size={21} /></span>
                <h3>{title}</h3>
                <p>{text}</p>
                {index < workflow.length - 1 && <ChevronRight className="workflow-arrow" size={19} />}
              </article>
            ))}
          </div>
        </section>

        <section className="public-cta">
          <div>
            <span className="eyebrow eyebrow--dark">Seu próximo projeto começa aqui</span>
            <h2>Mais precisão no projeto. Mais confiança na produção.</h2>
          </div>
          <Link className="button button--light button--large" to="/auth">Experimentar o Calcme 3D <ArrowRight size={18} /></Link>
        </section>
      </main>
      <footer className="public-footer">
        <Logo inverse />
        <span>Planejamento técnico conectado à produção.</span>
        <nav><Link to="/contact">Contato</Link><Link to="/privacy">Privacidade</Link><Link to="/terms">Termos</Link><Link to="/cookies">Cookies</Link></nav>
      </footer>
    </div>
  );
}
